import { useMemo, forwardRef } from "react";

// Forward ref to allow parent to attach the outside click ref
const EditableInput = forwardRef(
  (
    {
      value,
      editConfig = {},
      tableInstance,
      columnId,
      maxStringLength,
      className,
    },
    ref
  ) => {
    const uniqueValues = useMemo(() => {
      if (!tableInstance || !columnId) return [];
      const column = tableInstance.getColumn(columnId);
      if (!column) return [];

      // Safely get unique values, handling the case where getFacetedUniqueValues() might return undefined
      const facetedValues = column.getFacetedUniqueValues();
      if (!facetedValues) {
        // If faceted values are not available (which can happen with multiple row selection),
        // we can get unique values directly from the table data
        const allRows = tableInstance.getPreFilteredRowModel().rows;
        const uniqueSet = new Set(
          allRows
            .map((row) => row.getValue(columnId))
            .filter((value) => value != null)
        );
        return Array.from(uniqueSet).sort((a, b) => {
          if (typeof a === "string" && typeof b === "string") {
            return a.localeCompare(b);
          }
          return String(a).localeCompare(String(b));
        });
      }

      return Array.from(facetedValues.keys())
        .filter((value) => value != null)
        .sort((a, b) => {
          if (typeof a === "string" && typeof b === "string") {
            return a.localeCompare(b);
          }
          return String(a).localeCompare(String(b));
        });
    }, [tableInstance, columnId]);

    const placeholder = editConfig.placeholder;

    const renderInput = () => {
      if (editConfig.inputType === "select") {
        return (
          <select
            autoFocus
            value={value}
            onChange={(e) => editConfig.onChange?.(e.target.value)}
            className="border rounded px-2 py-1 mr-2"
            onClick={(e) => e.stopPropagation()}
          >
            {uniqueValues.map((value) => (
              <option key={String(value)} value={String(value)}>
                {value}
              </option>
            ))}
          </select>
        );
      }

      // For text inputs, add character count display
      return (
        <div className="flex flex-col w-full">
          <input
            placeholder={placeholder}
            maxLength={maxStringLength}
            autoFocus
            type={editConfig.inputType || "text"}
            value={value}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => editConfig.onChange?.(e.target.value)}
            className="border rounded px-2 py-1 mr-2 w-full"
            {...(editConfig.inputProps || {})}
          />
          {maxStringLength && (
            <div className="text-xs text-gray-500 mt-1">
              {value.length}/{maxStringLength} characters
            </div>
          )}
        </div>
      );
    };

    return (
      <div
        className={`flex items-center gap-2 flex-wrap w-full ${className}`}
        ref={ref} // This is the ref from useOutsideClick
      >
        <div className="flex-grow min-w-0">{renderInput()}</div>
      </div>
    );
  }
);

EditableInput.displayName = "EditableInput";

export default EditableInput;
